import type { CalendarEvent, CalendarView, EventColor } from '@/types/calendar';
import { CalendarDate, endOfMonth, endOfWeek, getWeeksInMonth, startOfMonth, startOfWeek, type DateValue } from '@internationalized/date';

/**
 * Get the start and end dates for a given calendar view and date
 */
export function getDateRange(view: CalendarView, date: DateValue): { start: DateValue; end: DateValue } {
    const locale = 'en-GB'; // Monday start

    switch (view) {
        case 'day':
            return { start: date, end: date };

        case 'week': {
            const start = startOfWeek(date, locale);
            const end = endOfWeek(date, locale);
            return { start, end };
        }

        case 'month': {
            // Get the full visible date range including days from prev/next months
            const locale = 'en-GB'; // Monday start
            const monthStart = startOfMonth(date);
            const monthEnd = endOfMonth(date);
            // First visible day is the Monday of the week containing the 1st
            const start = startOfWeek(monthStart, locale);
            // Last visible day is the Sunday of the week containing the last day
            const end = endOfWeek(monthEnd, locale);
            return { start, end };
        }

        default:
            return { start: date, end: date };
    }
}

/**
 * Format a DateValue to YYYY-MM-DD string
 */
export function formatDateToString(date: DateValue): string {
    const year = date.year.toString().padStart(4, '0');
    const month = date.month.toString().padStart(2, '0');
    const day = date.day.toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD string to DateValue
 */
export function parseDateString(dateString: string): DateValue {
    const [year, month, day] = dateString.split('-').map(Number);
    return new CalendarDate(year, month, day);
}

/**
 * Format date header based on view
 */
export function formatDateHeader(date: DateValue, view: CalendarView): string {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    switch (view) {
        case 'day': {
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const jsDate = new Date(date.year, date.month - 1, date.day);
            const dayName = dayNames[jsDate.getDay()];
            return `${dayName}, ${monthNames[date.month - 1]} ${date.day}, ${date.year}`;
        }

        case 'week': {
            const locale = 'en-GB';
            const weekStart = startOfWeek(date, locale);
            const weekEnd = endOfWeek(date, locale);

            if (weekStart.month === weekEnd.month) {
                return `${monthNames[weekStart.month - 1]} ${weekStart.day} - ${weekEnd.day}, ${weekStart.year}`;
            } else if (weekStart.year === weekEnd.year) {
                return `${monthNames[weekStart.month - 1]} ${weekStart.day} - ${monthNames[weekEnd.month - 1]} ${weekEnd.day}, ${weekStart.year}`;
            } else {
                return `${monthNames[weekStart.month - 1]} ${weekStart.day}, ${weekStart.year} - ${monthNames[weekEnd.month - 1]} ${weekEnd.day}, ${weekEnd.year}`;
            }
        }

        case 'month':
            return `${monthNames[date.month - 1]} ${date.year}`;

        default:
            return '';
    }
}

/**
 * Format day of week abbreviation
 */
export function formatDayOfWeek(date: DateValue): string {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const jsDate = new Date(date.year, date.month - 1, date.day);
    return dayNames[jsDate.getDay()];
}

/**
 * Format day number
 */
export function formatDayNumber(date: DateValue): string {
    return date.day.toString();
}

/**
 * Format hour as 12-hour time
 */
export function formatHour(hour: number): string {
    if (hour === 0) {
        return '12:00 AM';
    }
    if (hour < 12) {
        return `${hour}:00 AM`;
    }
    if (hour === 12) {
        return '12:00 PM';
    }
    return `${hour - 12}:00 PM`;
}

/**
 * Get today's date as CalendarDate
 */
export function getToday(): DateValue {
    const now = new Date();
    return new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/**
 * Get the previous period date based on view
 */
export function getPreviousPeriod(date: DateValue, view: CalendarView): DateValue {
    switch (view) {
        case 'day':
            return date.subtract({ days: 1 });

        case 'week':
            return date.subtract({ weeks: 1 });

        case 'month':
            return date.subtract({ months: 1 });

        default:
            return date;
    }
}

/**
 * Get the next period date based on view
 */
export function getNextPeriod(date: DateValue, view: CalendarView): DateValue {
    switch (view) {
        case 'day':
            return date.add({ days: 1 });

        case 'week':
            return date.add({ weeks: 1 });

        case 'month':
            return date.add({ months: 1 });

        default:
            return date;
    }
}

/**
 * Get an array of 7 dates for the week starting Monday
 */
export function getWeekDates(date: DateValue): DateValue[] {
    const locale = 'en-GB'; // Monday start
    const weekStart = startOfWeek(date, locale);
    const dates: DateValue[] = [];

    for (let i = 0; i < 7; i++) {
        dates.push(weekStart.add({ days: i }));
    }

    return dates;
}

/**
 * Get an array of weeks for the month, each week is an array of 7 dates
 */
export function getMonthWeeks(date: DateValue): DateValue[][] {
    const locale = 'en-GB'; // Monday start
    const monthStart = startOfMonth(date);
    const weeksCount = getWeeksInMonth(date, locale);
    const weeks: DateValue[][] = [];

    let currentWeekStart = startOfWeek(monthStart, locale);

    for (let i = 0; i < weeksCount; i++) {
        const week: DateValue[] = [];
        for (let j = 0; j < 7; j++) {
            week.push(currentWeekStart.add({ days: j }));
        }
        weeks.push(week);
        currentWeekStart = currentWeekStart.add({ weeks: 1 });
    }

    return weeks;
}

/**
 * Check if a date is in the same month as another date
 */
export function isInMonth(date: DateValue, month: DateValue): boolean {
    return date.year === month.year && date.month === month.month;
}

/**
 * Check if a date is today
 */
export function isToday(date: DateValue): boolean {
    const today = getToday();
    return date.year === today.year && date.month === today.month && date.day === today.day;
}

/**
 * Check if an event spans a given date
 */
export function eventSpansDate(event: CalendarEvent, date: DateValue): boolean {
    if (!event.start || !event.end) {
        return false;
    }
    const eventStart = parseDateString(event.start.split('T')[0]);
    const eventEnd = parseDateString(event.end.split('T')[0]);

    return date.compare(eventStart) >= 0 && date.compare(eventEnd) <= 0;
}

/**
 * Get all events for a specific date
 */
export function getEventsForDate(events: CalendarEvent[], date: DateValue): CalendarEvent[] {
    return events.filter((event) => eventSpansDate(event, date));
}

/**
 * Get the week span information for an event
 * Returns the starting column index and span length, or null if event doesn't span this week
 */
export function getEventWeekSpan(event: CalendarEvent, weekDates: DateValue[]): { startCol: number; span: number } | null {
    if (!event.start || !event.end) {
        return null;
    }
    const eventStart = parseDateString(event.start.split('T')[0]);
    const eventEnd = parseDateString(event.end.split('T')[0]);

    const weekStart = weekDates[0];
    const weekEnd = weekDates[6];

    // Event doesn't intersect this week
    if (eventEnd.compare(weekStart) < 0 || eventStart.compare(weekEnd) > 0) {
        return null;
    }

    // Find the starting column
    let startCol = 0;
    for (let i = 0; i < weekDates.length; i++) {
        if (eventStart.compare(weekDates[i]) <= 0) {
            startCol = i;
            break;
        }
        if (eventStart.compare(weekDates[i]) === 0) {
            startCol = i;
            break;
        }
    }

    // Find the span length
    let span = 0;
    for (let i = startCol; i < weekDates.length; i++) {
        if (eventEnd.compare(weekDates[i]) >= 0) {
            span++;
        } else {
            break;
        }
    }

    return { startCol, span };
}

/**
 * Get CSS classes for event colors using the app's event color system
 * Uses the --event-* CSS variables defined in app.css
 */
export function getEventColorClasses(color: EventColor | string, variant: 'solid' | 'dashed' | 'strikethrough' = 'solid'): string {
    // Map color names to the app's event color CSS variable names
    // These match the --event-* variables in app.css
    const validColors = [
        'blue',
        'gray',
        'purple',
        'red',
        'green',
        'yellow',
        'orange',
        'cyan',
        'emerald',
        'lime',
        'teal',
        'indigo',
        'violet',
        'pink',
        'rose',
        'amber',
        'magenta',
        'slate',
    ];

    const colorName = validColors.includes(color) ? color : 'blue';

    // Use the event color CSS variables from app.css
    // Text is neutral black/white (matching kanban and scheduler) rather than tinted per color
    const baseClasses = `bg-event-${colorName} text-foreground border border-black/20 dark:border-white/20`;

    if (variant === 'dashed') {
        return `${baseClasses} border-2 border-dashed`;
    }

    if (variant === 'strikethrough') {
        return `${baseClasses} opacity-50 line-through`;
    }

    return baseClasses;
}

/**
 * Get an array of hours for the day view
 */
export function getHoursArray(startHour = 0, endHour = 23): number[] {
    const hours: number[] = [];
    for (let i = startHour; i <= endHour; i++) {
        hours.push(i);
    }
    return hours;
}

/**
 * Format a date string for display in popovers
 */
export function formatDate(dateString: string): string {
    const date = parseDateString(dateString);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    return `${monthNames[date.month - 1]} ${date.day}, ${date.year}`;
}
